/**
 * DEC5 target text on box 9 (two table columns + a row note) and box 3 (a line
 * under the value). The status word comes from the server's verdict only.
 *
 * @module components/looker/TargetCells
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatCurrency } from '@/config/brand';
import { platformLabel } from '@/utils/platformLabel';
import type { LookerTile, TileTargetCell, TileTargets } from '@/services/looker/lookerTypes';
import { formatTargetDelta, hasVerdict, RESULT_METRIC_NAME, TARGET_TONE } from '@/services/looker/lookerTargetCells';

/** Literal keys, so the locale integrity test sees them. */
const STATUS_WORD: Record<TileTargetCell['status'], (t: TFunction) => string> = {
  met: (t) => t('looker.targets.status.met'),
  near: (t) => t('looker.targets.status.near'),
  over25: (t) => t('looker.targets.status.over25'),
  none: (t) => t('looker.targets.status.none'),
  na: (t) => t('looker.targets.status.na'),
  failed: (t) => t('looker.targets.status.failed'),
  noCurrency: (t) => t('looker.targets.status.noCurrency'),
  otherMetric: (t) => t('looker.targets.status.otherMetric'),
};

/** "ใกล้เป้า +14.8%"; none names the page currency when there is one; otherMetric names the definition the target was set against. */
export const targetStatusText = (t: TFunction, cell: TileTargetCell, currency: string | null, channel = ''): string => {
  if (cell.status === 'otherMetric' && cell.setAgainst) return `${t('looker.targets.otherMetricFor', { metric: RESULT_METRIC_NAME[cell.setAgainst] })}${channel}`;
  if (cell.status === 'none' && currency) return t('looker.targets.noneFor', { currency });
  const word = STATUS_WORD[cell.status](t);
  return hasVerdict(cell) ? `${word} ${formatTargetDelta(cell.deltaPct)}` : word;
};

/** A target exists only with a page currency (else every cell is noCurrency), so currency is never defaulted. */
const money = (n: number, currency: string): string => formatCurrency(n, true, currency);

/** I1: on mixed keys the header is generic, so a cell that used a channel's own target names it. */
const channelNote = (cell: TileTargetCell, targets: TileTargets): string =>
  (cell.platform && !targets.platform ? ` (${platformLabel(cell.platform)})` : '');

const changedText = (t: TFunction, cell: TileTargetCell, currency: string): string | null =>
  (cell.changed ? t('looker.targets.changed', { from: money(cell.changed.from, currency), to: money(cell.changed.to, currency), on: cell.changed.on }) : null);

export const targetHeader = (t: TFunction, targets: TileTargets): string =>
  targets.platform ? t('looker.targets.columnFor', { channel: platformLabel(targets.platform) }) : t('looker.targets.column');

export const TargetValue: React.FC<{ cell: TileTargetCell | undefined; targets: TileTargets }> = ({ cell, targets }) =>
  <>{cell && cell.target !== null && targets.currency !== null ? `${money(cell.target, targets.currency)}${channelNote(cell, targets)}` : '—'}</>;

export const TargetStatus: React.FC<{ cell: TileTargetCell | undefined; targets: TileTargets }> = ({ cell, targets }) => {
  const { t } = useTranslation();
  if (!cell) return <>—</>;
  return (
    <>
      <span className={`font-semibold ${TARGET_TONE[cell.status]}`}>{targetStatusText(t, cell, targets.currency, channelNote(cell, targets))}</span>
      {targets.currency !== null && cell.changed && (
        <span className="block text-xs font-normal text-neutral-700">{changedText(t, cell, targets.currency)}</span>
      )}
    </>
  );
};

/** Box 3: "เป้า ฿80.00 · ใกล้เป้า +14.8%"; with no target, the status alone. */
export const TargetLine: React.FC<{ tile: Pick<LookerTile, 'targets'> }> = ({ tile }) => {
  const { t } = useTranslation();
  const targets = tile.targets;
  const cell = targets ? Object.values(targets.byObjective)[0] : undefined;
  if (!targets || !cell) return null;
  const status = targetStatusText(t, cell, targets.currency, channelNote(cell, targets));
  const { currency } = targets;
  return (
    <div className="-mt-2 pb-2 text-sm">
      <p className={TARGET_TONE[cell.status]}>
        {cell.target !== null && currency !== null
          ? t('looker.targets.line', { target: `${money(cell.target, currency)}${cell.platform ? ` (${platformLabel(cell.platform)})` : ''}`, status })
          : status}
      </p>
      {currency !== null && cell.changed && <p className="text-xs text-neutral-700">{changedText(t, cell, currency)}</p>}
    </div>
  );
};
